import Alert from '@mui/material/Alert';
import AlertTitle from '@mui/material/AlertTitle';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import FormHelperText from '@mui/material/FormHelperText';
import FormLabel from '@mui/material/FormLabel';
import InputLabel from '@mui/material/InputLabel';
import LinearProgress from '@mui/material/LinearProgress';
import Link from '@mui/material/Link';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Paper from '@mui/material/Paper';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { useEffect, useState } from 'react';
import { Loader } from '../../components/1elements';
import ApproverSearch from '../../components/2compounds/ApproverSearch';
import {
  getDashboardErrorMessage,
  useCreateDashboardFeedbackMutation,
  useDeleteDashboardFeedbackMutation,
  useGetDashboardFeedbackQuery,
  useListDashboardFeedbackQuery,
  useTransitionDashboardFeedbackMutation,
  useUpdateDashboardFeedbackMutation
} from './dashboardApi';
import { DASHBOARD_FEEDBACK_STATUS } from './dashboardContract';
import {
  DASHBOARD_FEEDBACK_CATEGORY_OPTIONS,
  DASHBOARD_FEEDBACK_CONTACT_EMAIL_MAX_LENGTH,
  DASHBOARD_FEEDBACK_CONTACT_NAME_MAX_LENGTH,
  DASHBOARD_FEEDBACK_CONTACT_PHONE_MAX_LENGTH,
  DASHBOARD_FEEDBACK_DELETE_REASON_MAX_LENGTH,
  DASHBOARD_FEEDBACK_PAGE_SIZE,
  DASHBOARD_FEEDBACK_RESOLUTION_MAX_LENGTH,
  DASHBOARD_FEEDBACK_TITLE_MAX_LENGTH,
  createDashboardFeedbackForm,
  createEmptyDashboardFeedbackForm,
  formatDashboardFeedbackDate,
  formatDashboardFeedbackTimestamp,
  getDashboardFeedbackCategoryLabel,
  getDashboardFeedbackStatusColour,
  getDashboardFeedbackStatusLabel,
  getDashboardFeedbackTransitions,
  validateDashboardFeedbackDelete,
  validateDashboardFeedbackForm,
  validateDashboardFeedbackTransition
} from './dashboardFeedbackModel';
import {
  canRemoveDashboardData,
  canUpdateDashboardData,
  canViewDashboardData
} from './dashboardPermissions';

const DIALOG_MODE = Object.freeze({
  CREATE: 'create',
  EDIT: 'edit',
  STATUS: 'status',
  DELETE: 'delete',
  DETAILS: 'details'
});

const feedbackTitle = (feedback) => feedback.title || 'Feedback';

const feedbackOwnerName = (feedback) => {
  if (feedback.ownerName) return feedback.ownerName;
  return feedback.ownerUserId ? 'Owner name unavailable' : 'Unassigned';
};

const FeedbackContactSummary = ({ contact }) => {
  const hasContact = Boolean(contact?.name || contact?.email || contact?.phone);

  if (!hasContact) {
    return (
      <Typography color="text.secondary" variant="body2">
        No contact details provided.
      </Typography>
    );
  }

  return (
    <Stack spacing={0.25}>
      {contact.name && <Typography variant="body2">{contact.name}</Typography>}
      {contact.email && (
        <Link href={'mailto:' + contact.email} variant="body2">
          {contact.email}
        </Link>
      )}
      {contact.phone && (
        <Link href={'tel:' + contact.phone} variant="body2">
          {contact.phone}
        </Link>
      )}
    </Stack>
  );
};

const ApproverField = ({
  approver,
  error,
  locationId,
  onChange,
  supportingText = 'This request will remain pending until the selected approver reviews it.'
}) => (
  <FormControl error={Boolean(error)} fullWidth>
    <FormLabel required>Approver</FormLabel>
    <Box
      sx={{
        '& > div': { maxWidth: 'none' },
        mt: 0.75,
        width: '100%'
      }}
    >
      <ApproverSearch
        approver={approver}
        dashboard
        locationId={locationId}
        setApprover={onChange}
        showError={Boolean(error)}
      />
    </Box>
    <FormHelperText>{error || supportingText}</FormHelperText>
  </FormControl>
);

const FeedbackFormFields = ({
  approver,
  errors,
  form,
  locationId,
  onApproverChange,
  onFieldChange,
  onOwnerChange,
  onOwnerClear,
  owner,
  today
}) => (
  <Stack spacing={2} sx={{ pt: 1 }}>
    <TextField
      error={Boolean(errors.title)}
      helperText={
        errors.title ||
        String(form.title.length) +
          '/' +
          String(DASHBOARD_FEEDBACK_TITLE_MAX_LENGTH)
      }
      inputProps={{ maxLength: DASHBOARD_FEEDBACK_TITLE_MAX_LENGTH }}
      label="Title (optional)"
      onChange={(event) => onFieldChange('title', event.target.value)}
      value={form.title}
    />
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }
      }}
    >
      <FormControl>
        <InputLabel id="dashboard-feedback-category-label">
          Category (optional)
        </InputLabel>
        <Select
          label="Category (optional)"
          labelId="dashboard-feedback-category-label"
          onChange={(event) => onFieldChange('category', event.target.value)}
          value={form.category}
        >
          <MenuItem value="">Uncategorized</MenuItem>
          {DASHBOARD_FEEDBACK_CATEGORY_OPTIONS.map((option) => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
      <TextField
        error={Boolean(errors.receivedDate)}
        helperText={errors.receivedDate}
        InputLabelProps={{ shrink: true }}
        inputProps={{ max: today }}
        label="Date received"
        onChange={(event) => onFieldChange('receivedDate', event.target.value)}
        required
        type="date"
        value={form.receivedDate}
      />
    </Box>
    <TextField
      error={Boolean(errors.comment)}
      helperText={errors.comment}
      label="Feedback details"
      minRows={4}
      multiline
      onChange={(event) => onFieldChange('comment', event.target.value)}
      required
      value={form.comment}
    />
    <Box>
      <Typography fontWeight={700}>Contact details</Typography>
      <Typography color="text.secondary" variant="body2">
        Optional details for following up with the person who provided the
        feedback.
      </Typography>
    </Box>
    <TextField
      error={Boolean(errors.contactName)}
      helperText={errors.contactName}
      inputProps={{ maxLength: DASHBOARD_FEEDBACK_CONTACT_NAME_MAX_LENGTH }}
      label="Contact name (optional)"
      onChange={(event) => onFieldChange('contactName', event.target.value)}
      value={form.contactName}
    />
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' }
      }}
    >
      <TextField
        error={Boolean(errors.contactEmail)}
        helperText={errors.contactEmail}
        inputProps={{ maxLength: DASHBOARD_FEEDBACK_CONTACT_EMAIL_MAX_LENGTH }}
        label="Contact email (optional)"
        onChange={(event) => onFieldChange('contactEmail', event.target.value)}
        type="email"
        value={form.contactEmail}
      />
      <TextField
        error={Boolean(errors.contactPhone)}
        helperText={errors.contactPhone}
        inputProps={{ maxLength: DASHBOARD_FEEDBACK_CONTACT_PHONE_MAX_LENGTH }}
        label="Contact phone (optional)"
        onChange={(event) => onFieldChange('contactPhone', event.target.value)}
        value={form.contactPhone}
      />
    </Box>
    <FormControl fullWidth>
      <FormLabel>Follow-up owner (optional)</FormLabel>
      <Box
        sx={{
          '& > div': { maxWidth: 'none' },
          mt: 0.75,
          width: '100%'
        }}
      >
        <ApproverSearch
          approver={owner}
          setApprover={onOwnerChange}
          showError={false}
        />
      </Box>
      <Stack
        alignItems="center"
        direction="row"
        justifyContent="space-between"
        spacing={1}
      >
        <FormHelperText>
          {owner?.username
            ? 'Assigned to ' + owner.username + '.'
            : form.ownerUserId
              ? 'An owner is currently assigned. Search to replace them.'
              : 'Search for a user now, or leave this unassigned.'}
        </FormHelperText>
        {form.ownerUserId && (
          <Button onClick={onOwnerClear} size="small">
            Clear owner
          </Button>
        )}
      </Stack>
    </FormControl>
    <Box>
      <Typography fontWeight={700}>Approval</Typography>
      <Typography color="text.secondary" variant="body2">
        New and edited feedback is published only after approval.
      </Typography>
    </Box>
    <ApproverField
      approver={approver}
      error={errors.approver}
      locationId={locationId}
      onChange={onApproverChange}
    />
  </Stack>
);

const DashboardFeedbackCard = ({
  canRemove,
  canUpdate,
  feedback,
  onDelete,
  onDetails,
  onEdit,
  onStatus
}) => {
  const transitions = getDashboardFeedbackTransitions(feedback.feedbackStatus);
  const title = feedbackTitle(feedback);

  return (
    <Paper
      component="article"
      elevation={0}
      sx={{ border: '1px solid', borderColor: 'divider', p: 2 }}
    >
      <Stack
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1}
      >
        <Box>
          <Typography component="h3" fontSize={17} fontWeight={700}>
            {title}
          </Typography>
          <Typography color="text.secondary" variant="body2">
            {getDashboardFeedbackCategoryLabel(feedback.category)} · Received{' '}
            {formatDashboardFeedbackDate(feedback.receivedDate)}
          </Typography>
        </Box>
        <Chip
          color={getDashboardFeedbackStatusColour(feedback.feedbackStatus)}
          label={getDashboardFeedbackStatusLabel(feedback.feedbackStatus)}
          size="small"
          variant="outlined"
        />
      </Stack>
      <Typography
        sx={{ mt: 1.5, overflowWrap: 'anywhere', whiteSpace: 'pre-wrap' }}
        variant="body2"
      >
        {feedback.comment}
      </Typography>
      <Box
        sx={{
          display: 'grid',
          gap: 2,
          gridTemplateColumns: { xs: '1fr', sm: 'minmax(0, 2fr) minmax(0, 1fr)' },
          mt: 2
        }}
      >
        <Box>
          <Typography color="text.secondary" fontWeight={700} variant="caption">
            CONTACT DETAILS
          </Typography>
          <Box sx={{ mt: 0.5 }}>
            <FeedbackContactSummary contact={feedback.contact} />
          </Box>
        </Box>
        <Box>
          <Typography color="text.secondary" fontWeight={700} variant="caption">
            FOLLOW-UP OWNER
          </Typography>
          <Typography sx={{ mt: 0.5 }} variant="body2">
            {feedbackOwnerName(feedback)}
          </Typography>
        </Box>
      </Box>
      {feedback.resolutionNotes && (
        <Alert severity="success" sx={{ mt: 1.5 }}>
          <AlertTitle>Resolution</AlertTitle>
          {feedback.resolutionNotes}
        </Alert>
      )}
      <Stack
        direction="row"
        flexWrap="wrap"
        gap={1}
        justifyContent="flex-end"
        sx={{ mt: 2 }}
      >
        <Button
          aria-label={'View details for ' + title}
          onClick={() => onDetails(feedback)}
          size="small"
        >
          View details
        </Button>
        {canUpdate && (
          <Button
            aria-label={'Edit ' + title}
            onClick={() => onEdit(feedback)}
            size="small"
          >
            Edit
          </Button>
        )}
        {canUpdate && transitions.length > 0 && (
          <Button
            aria-label={'Update status for ' + title}
            onClick={() => onStatus(feedback)}
            size="small"
            variant="outlined"
          >
            Update status
          </Button>
        )}
        {canRemove && (
          <Button
            aria-label={'Remove ' + title}
            color="error"
            onClick={() => onDelete(feedback)}
            size="small"
          >
            Remove
          </Button>
        )}
      </Stack>
    </Paper>
  );
};

const FeedbackDetails = ({ feedback, isLoading }) => {
  if (isLoading) return <Loader />;
  if (!feedback) return null;

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <Stack alignItems="center" direction="row" flexWrap="wrap" gap={1}>
        <Chip
          color={getDashboardFeedbackStatusColour(feedback.feedbackStatus)}
          label={getDashboardFeedbackStatusLabel(feedback.feedbackStatus)}
          size="small"
        />
        <Chip
          label={getDashboardFeedbackCategoryLabel(feedback.category)}
          size="small"
          variant="outlined"
        />
        <Typography color="text.secondary" variant="body2">
          Received {formatDashboardFeedbackDate(feedback.receivedDate)}
        </Typography>
      </Stack>
      <Box>
        <Typography color="text.secondary" fontWeight={600} variant="caption">
          FEEDBACK
        </Typography>
        <Typography sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }}>
          {feedback.comment}
        </Typography>
      </Box>
      <Box>
        <Typography fontWeight={700}>Contact details</Typography>
        <Box sx={{ mt: 0.5 }}>
          <FeedbackContactSummary contact={feedback.contact} />
        </Box>
      </Box>
      <Box>
        <Typography fontWeight={700}>Follow-up owner</Typography>
        <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
          {feedbackOwnerName(feedback)}
        </Typography>
      </Box>
      {feedback.resolutionNotes && (
        <Box>
          <Typography fontWeight={700}>Resolution</Typography>
          <Typography sx={{ mt: 0.5, whiteSpace: 'pre-wrap' }} variant="body2">
            {feedback.resolutionNotes}
          </Typography>
        </Box>
      )}
      <Box>
        <Typography fontWeight={700}>Status history</Typography>
        {(feedback.history ?? []).length > 0 ? (
          <Stack spacing={1} sx={{ mt: 1 }}>
            {feedback.history.map((event) => (
              <Box
                key={event.feedbackStatusEventId}
                sx={{
                  borderLeft: '3px solid',
                  borderColor: 'divider',
                  pl: 1.5
                }}
              >
                <Typography fontWeight={600} variant="body2">
                  {event.fromStatus
                    ? getDashboardFeedbackStatusLabel(event.fromStatus) +
                      ' to ' +
                      getDashboardFeedbackStatusLabel(event.toStatus)
                    : 'Recorded as ' +
                      getDashboardFeedbackStatusLabel(event.toStatus)}
                </Typography>
                <Typography color="text.secondary" variant="caption">
                  {formatDashboardFeedbackTimestamp(event.occurredAt)}
                </Typography>
                {event.note && (
                  <Typography sx={{ mt: 0.25 }} variant="body2">
                    {event.note}
                  </Typography>
                )}
              </Box>
            ))}
          </Stack>
        ) : (
          <Typography color="text.secondary" sx={{ mt: 0.5 }} variant="body2">
            No status changes recorded.
          </Typography>
        )}
      </Box>
    </Stack>
  );
};

const DashboardFeedback = ({ locationId }) => {
  const canView = canViewDashboardData();
  const canUpdate = canUpdateDashboardData();
  const canRemove = canRemoveDashboardData();
  const today = dayjs().format('YYYY-MM-DD');
  const [page, setPage] = useState(1);
  const [dialogMode, setDialogMode] = useState(null);
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [form, setForm] = useState(createEmptyDashboardFeedbackForm(today));
  const [formErrors, setFormErrors] = useState({});
  const [approver, setApprover] = useState(null);
  const [owner, setOwner] = useState(null);
  const [nextStatus, setNextStatus] = useState('');
  const [resolutionNotes, setResolutionNotes] = useState('');
  const [statusError, setStatusError] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteErrors, setDeleteErrors] = useState({});
  const [successMessage, setSuccessMessage] = useState(null);

  const feedbackQuery = useListDashboardFeedbackQuery(
    { locationId, page, pageSize: DASHBOARD_FEEDBACK_PAGE_SIZE },
    { skip: !locationId || !canView }
  );
  const detailQuery = useGetDashboardFeedbackQuery(
    selectedFeedback?.feedbackId,
    {
      skip:
        !canView ||
        dialogMode !== DIALOG_MODE.DETAILS ||
        !selectedFeedback?.feedbackId
    }
  );
  const [createFeedback, createState] = useCreateDashboardFeedbackMutation();
  const [updateFeedback, updateState] = useUpdateDashboardFeedbackMutation();
  const [transitionFeedback, transitionState] =
    useTransitionDashboardFeedbackMutation();
  const [deleteFeedback, deleteState] = useDeleteDashboardFeedbackMutation();

  const items = feedbackQuery.data?.feedback?.items ?? [];
  const meta = feedbackQuery.data?.meta ?? {
    page,
    pageSize: DASHBOARD_FEEDBACK_PAGE_SIZE,
    totalItems: 0,
    totalPages: 0
  };
  const isSaving =
    createState.isLoading ||
    updateState.isLoading ||
    transitionState.isLoading ||
    deleteState.isLoading;
  const mutationError =
    dialogMode === DIALOG_MODE.CREATE
      ? createState.error
      : dialogMode === DIALOG_MODE.EDIT
        ? updateState.error
        : dialogMode === DIALOG_MODE.STATUS
          ? transitionState.error
          : dialogMode === DIALOG_MODE.DELETE
            ? deleteState.error
            : null;

  useEffect(() => {
    setPage(1);
  }, [locationId]);

  useEffect(() => {
    const lastPage = Math.max(1, meta.totalPages);
    if (page > lastPage) setPage(lastPage);
  }, [meta.totalPages, page]);

  const resetMutationStates = () => {
    createState.reset?.();
    updateState.reset?.();
    transitionState.reset?.();
    deleteState.reset?.();
  };

  const closeDialog = (force = false) => {
    if (isSaving && !force) return;
    setDialogMode(null);
    setSelectedFeedback(null);
    setFormErrors({});
    setApprover(null);
    setOwner(null);
    setNextStatus('');
    setResolutionNotes('');
    setStatusError(null);
    setDeleteReason('');
    setDeleteErrors({});
  };

  const openCreate = () => {
    resetMutationStates();
    setSelectedFeedback(null);
    setForm(createEmptyDashboardFeedbackForm(today));
    setFormErrors({});
    setApprover(null);
    setOwner(null);
    setSuccessMessage(null);
    setDialogMode(DIALOG_MODE.CREATE);
  };

  const openEdit = (feedback) => {
    resetMutationStates();
    setSelectedFeedback(feedback);
    setForm(createDashboardFeedbackForm(feedback));
    setFormErrors({});
    setApprover(null);
    setOwner(null);
    setSuccessMessage(null);
    setDialogMode(DIALOG_MODE.EDIT);
  };

  const openStatus = (feedback) => {
    resetMutationStates();
    setSelectedFeedback(feedback);
    setNextStatus(getDashboardFeedbackTransitions(feedback.feedbackStatus)[0]);
    setResolutionNotes('');
    setStatusError(null);
    setSuccessMessage(null);
    setDialogMode(DIALOG_MODE.STATUS);
  };

  const openDelete = (feedback) => {
    resetMutationStates();
    setSelectedFeedback(feedback);
    setDeleteReason('');
    setDeleteErrors({});
    setApprover(null);
    setSuccessMessage(null);
    setDialogMode(DIALOG_MODE.DELETE);
  };

  const openDetails = (feedback) => {
    setSelectedFeedback(feedback);
    setDialogMode(DIALOG_MODE.DETAILS);
  };

  const changeFormField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setFormErrors((current) => {
      if (!current[field]) return current;
      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const changeApprover = (value) => {
    setApprover(value);
    setFormErrors((current) => ({ ...current, approver: undefined }));
    setDeleteErrors((current) => ({ ...current, approver: undefined }));
  };

  const changeOwner = (value) => {
    setOwner(value);
    setForm((current) => ({
      ...current,
      ownerUserId: value?.user_id ?? null
    }));
  };

  const clearOwner = () => {
    setOwner(null);
    setForm((current) => ({ ...current, ownerUserId: null }));
  };

  const submitFeedbackForm = async (event) => {
    event.preventDefault();
    const validation = validateDashboardFeedbackForm({ approver, form });
    setFormErrors(validation.errors);
    if (!validation.isValid) return;

    try {
      if (dialogMode === DIALOG_MODE.CREATE) {
        await createFeedback({
          locationId,
          ...validation.payload
        }).unwrap();
        setSuccessMessage('Feedback submitted for approval.');
      } else {
        await updateFeedback({
          feedbackId: selectedFeedback.feedbackId,
          ...validation.payload
        }).unwrap();
        setSuccessMessage('Feedback changes submitted for approval.');
      }
      closeDialog(true);
    } catch (_error) {
      // RTK Query exposes the normalized error through the active mutation state.
    }
  };

  const submitStatus = async (event) => {
    event.preventDefault();
    const validation = validateDashboardFeedbackTransition(
      nextStatus,
      resolutionNotes
    );
    setStatusError(validation.error);
    if (!validation.isValid) return;

    try {
      await transitionFeedback({
        feedbackId: selectedFeedback.feedbackId,
        ...validation.payload
      }).unwrap();
      setSuccessMessage(
        'Feedback marked ' +
          getDashboardFeedbackStatusLabel(nextStatus).toLowerCase() +
          '.'
      );
      closeDialog(true);
    } catch (_error) {
      // RTK Query exposes the normalized error through transitionState.
    }
  };

  const submitDelete = async (event) => {
    event.preventDefault();
    const validation = validateDashboardFeedbackDelete(deleteReason, approver);
    setDeleteErrors(validation.errors);
    if (!validation.isValid) return;

    try {
      await deleteFeedback({
        feedbackId: selectedFeedback.feedbackId,
        ...validation.payload
      }).unwrap();
      setSuccessMessage('Feedback removal submitted for approval.');
      closeDialog(true);
    } catch (_error) {
      // RTK Query exposes the normalized error through deleteState.
    }
  };

  if (!canView || !locationId) return null;
  if (feedbackQuery.isLoading) {
    return (
      <Box data-testid="dashboard-feedback" sx={{ mt: 4 }}>
        <Loader />
      </Box>
    );
  }

  const details =
    detailQuery.data?.feedback?.item ?? selectedFeedback ?? undefined;
  const availableTransitions = selectedFeedback
    ? getDashboardFeedbackTransitions(selectedFeedback.feedbackStatus)
    : [];
  const needsResolution =
    nextStatus === DASHBOARD_FEEDBACK_STATUS.RESOLVED ||
    nextStatus === DASHBOARD_FEEDBACK_STATUS.NO_ACTION;

  return (
    <Box data-testid="dashboard-feedback" sx={{ mt: 4 }}>
      <Stack
        alignItems={{ xs: 'flex-start', sm: 'center' }}
        direction={{ xs: 'column', sm: 'row' }}
        justifyContent="space-between"
        spacing={1.5}
      >
        <Box>
          <Typography component="h2" fontSize={18} fontWeight={700}>
            Comments and feedback
          </Typography>
          <Typography color="text.secondary" variant="body2">
            Feedback is kept separately from dashboard metrics and remains
            available throughout its follow-up lifecycle.
          </Typography>
        </Box>
        {canUpdate && (
          <Button onClick={openCreate} variant="contained">
            Record feedback
          </Button>
        )}
      </Stack>
      {feedbackQuery.isFetching && <LinearProgress sx={{ mt: 2 }} />}
      {successMessage && (
        <Alert
          onClose={() => setSuccessMessage(null)}
          severity="success"
          sx={{ mt: 2 }}
        >
          {successMessage}
        </Alert>
      )}
      {feedbackQuery.error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          <AlertTitle>Feedback could not be loaded</AlertTitle>
          {getDashboardErrorMessage(feedbackQuery.error)}
          <Box sx={{ mt: 1 }}>
            <Button onClick={() => feedbackQuery.refetch()} size="small">
              Try again
            </Button>
          </Box>
        </Alert>
      )}
      {!feedbackQuery.error && items.length === 0 ? (
        <Paper
          elevation={0}
          sx={{ border: '1px solid', borderColor: 'divider', mt: 2, p: 3 }}
        >
          <Typography color="text.secondary">
            No feedback has been recorded for this location.
          </Typography>
        </Paper>
      ) : (
        !feedbackQuery.error && (
          <>
            <Typography color="text.secondary" sx={{ mt: 2 }} variant="body2">
              {meta.totalItems}{' '}
              {meta.totalItems === 1 ? 'feedback record' : 'feedback records'}
            </Typography>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {items.map((feedback) => (
                <DashboardFeedbackCard
                  canRemove={canRemove}
                  canUpdate={canUpdate}
                  feedback={feedback}
                  key={feedback.feedbackId}
                  onDelete={openDelete}
                  onDetails={openDetails}
                  onEdit={openEdit}
                  onStatus={openStatus}
                />
              ))}
            </Stack>
            {meta.totalPages > 1 && (
              <Stack alignItems="center" sx={{ mt: 2 }}>
                <Pagination
                  count={meta.totalPages}
                  onChange={(_event, nextPage) => setPage(nextPage)}
                  page={page}
                  showFirstButton
                  showLastButton
                />
              </Stack>
            )}
          </>
        )
      )}

      <Dialog
        fullWidth
        maxWidth="md"
        onClose={() => closeDialog()}
        open={
          dialogMode === DIALOG_MODE.CREATE || dialogMode === DIALOG_MODE.EDIT
        }
      >
        <Box component="form" noValidate onSubmit={submitFeedbackForm}>
          <DialogTitle>
            {dialogMode === DIALOG_MODE.CREATE
              ? 'Record feedback'
              : 'Edit feedback'}
          </DialogTitle>
          <DialogContent>
            <FeedbackFormFields
              approver={approver}
              errors={formErrors}
              form={form}
              locationId={locationId}
              onApproverChange={changeApprover}
              onFieldChange={changeFormField}
              onOwnerChange={changeOwner}
              onOwnerClear={clearOwner}
              owner={owner}
              today={today}
            />
            {mutationError && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {getDashboardErrorMessage(
                  mutationError,
                  'Feedback could not be submitted.'
                )}
              </Alert>
            )}
          </DialogContent>
          <DialogActions>
            <Button disabled={isSaving} onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button disabled={isSaving} type="submit" variant="contained">
              {isSaving
                ? 'Submitting...'
                : dialogMode === DIALOG_MODE.CREATE
                  ? 'Submit for approval'
                  : 'Submit changes'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => closeDialog()}
        open={dialogMode === DIALOG_MODE.STATUS}
      >
        <Box component="form" noValidate onSubmit={submitStatus}>
          <DialogTitle>Update feedback status</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Typography color="text.secondary" variant="body2">
                {selectedFeedback && feedbackTitle(selectedFeedback)}
              </Typography>
              <FormControl>
                <InputLabel id="dashboard-feedback-status-label">
                  New status
                </InputLabel>
                <Select
                  label="New status"
                  labelId="dashboard-feedback-status-label"
                  onChange={(event) => {
                    setNextStatus(event.target.value);
                    setResolutionNotes('');
                    setStatusError(null);
                  }}
                  value={nextStatus}
                >
                  {availableTransitions.map((status) => (
                    <MenuItem key={status} value={status}>
                      {getDashboardFeedbackStatusLabel(status)}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              {needsResolution && (
                <TextField
                  error={Boolean(statusError)}
                  helperText={
                    statusError ||
                    String(resolutionNotes.length) +
                      '/' +
                      String(DASHBOARD_FEEDBACK_RESOLUTION_MAX_LENGTH)
                  }
                  inputProps={{
                    maxLength: DASHBOARD_FEEDBACK_RESOLUTION_MAX_LENGTH
                  }}
                  label="Resolution notes"
                  minRows={3}
                  multiline
                  onChange={(event) => {
                    setResolutionNotes(event.target.value);
                    setStatusError(null);
                  }}
                  required
                  value={resolutionNotes}
                />
              )}
              {mutationError && (
                <Alert severity="error">
                  {getDashboardErrorMessage(
                    mutationError,
                    'Feedback status could not be updated.'
                  )}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={isSaving} onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button disabled={isSaving} type="submit" variant="contained">
              {isSaving ? 'Saving...' : 'Update status'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => closeDialog()}
        open={dialogMode === DIALOG_MODE.DELETE}
      >
        <Box component="form" noValidate onSubmit={submitDelete}>
          <DialogTitle>Remove feedback</DialogTitle>
          <DialogContent>
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="warning">
                This submits a removal request for approval. The record and its
                audit history are retained.
              </Alert>
              <TextField
                error={Boolean(deleteErrors.reason)}
                helperText={
                  deleteErrors.reason ||
                  String(deleteReason.length) +
                    '/' +
                    String(DASHBOARD_FEEDBACK_DELETE_REASON_MAX_LENGTH)
                }
                inputProps={{
                  maxLength: DASHBOARD_FEEDBACK_DELETE_REASON_MAX_LENGTH
                }}
                label="Reason for removal"
                minRows={3}
                multiline
                onChange={(event) => {
                  setDeleteReason(event.target.value);
                  setDeleteErrors((current) => ({
                    ...current,
                    reason: undefined
                  }));
                }}
                required
                value={deleteReason}
              />
              <ApproverField
                approver={approver}
                error={deleteErrors.approver}
                onChange={changeApprover}
              />
              {mutationError && (
                <Alert severity="error">
                  {getDashboardErrorMessage(
                    mutationError,
                    'Feedback removal could not be submitted.'
                  )}
                </Alert>
              )}
            </Stack>
          </DialogContent>
          <DialogActions>
            <Button disabled={isSaving} onClick={() => closeDialog()}>
              Cancel
            </Button>
            <Button
              color="error"
              disabled={isSaving}
              type="submit"
              variant="contained"
            >
              {isSaving ? 'Submitting...' : 'Submit removal'}
            </Button>
          </DialogActions>
        </Box>
      </Dialog>

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => closeDialog()}
        open={dialogMode === DIALOG_MODE.DETAILS}
      >
        <DialogTitle>
          {selectedFeedback ? feedbackTitle(selectedFeedback) : 'Feedback'}
        </DialogTitle>
        <DialogContent>
          {detailQuery.error && (
            <Alert severity="error">
              {getDashboardErrorMessage(
                detailQuery.error,
                'Feedback details could not be loaded.'
              )}
            </Alert>
          )}
          {!detailQuery.error && (
            <FeedbackDetails
              feedback={details}
              isLoading={detailQuery.isLoading}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => closeDialog()}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DashboardFeedback;
